import { useEffect, useState } from 'react'
import { handleApiError } from '../utils/apiErrorHandler'
import './Toast.css'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastAction {
  label: string
  onClick: () => void
  variant?: 'primary' | 'link'
}

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
  action?: ToastAction
  timestamp?: Date
}

let toastListeners: ((toast: Toast) => void)[] = []
let toasts: Toast[] = []

export function showToast(
  message: string,
  type: ToastType = 'info',
  duration = 5000,
  action?: ToastAction
) {
  const id = Math.random().toString(36).substring(7)
  const toast: Toast = { id, message, type, duration, action, timestamp: new Date() }
  toasts = [...toasts, toast]
  toastListeners.forEach(listener => listener(toast))

  if (duration > 0) {
    setTimeout(() => {
      removeToast(id)
    }, duration)
  }
}

/**
 * Show a user-friendly error toast from any caught error.
 * Uses apiErrorHandler to sanitize messages — never exposes raw backend errors.
 * Auto-cancelled requests (status 0) are silently ignored.
 */
export function showErrorToast(error: any, context?: string) {
  const apiError = handleApiError(error, context)
  if (apiError.message) {
    showToast(apiError.message, 'error')
  }
}

function removeToast(id: string) {
  toasts = toasts.filter(t => t.id !== id)
  const event = new CustomEvent('toast-removed', { detail: { id } })
  window.dispatchEvent(event)
}

function getIcon(type: ToastType) {
  switch (type) {
    case 'success':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      )
    case 'error':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      )
    case 'warning':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      )
    case 'info':
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      )
  }
}

function formatTimestamp(date: Date) {
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  if (diff < 60000) return 'Just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return date.toLocaleDateString()
}

export function ToastContainer() {
  const [activeToasts, setActiveToasts] = useState<Toast[]>([])

  useEffect(() => {
    const handleNewToast = (toast: Toast) => {
      setActiveToasts(prev => [...prev, toast])
    }

    toastListeners.push(handleNewToast)
    setActiveToasts(toasts)

    const handleRemove = (e: CustomEvent) => {
      setActiveToasts(prev => prev.filter(t => t.id !== e.detail.id))
    }

    window.addEventListener('toast-removed', handleRemove as EventListener)

    return () => {
      toastListeners = toastListeners.filter(l => l !== handleNewToast)
      window.removeEventListener('toast-removed', handleRemove as EventListener)
    }
  }, [])

  const handleClose = (id: string) => {
    const event = new CustomEvent('toast-removed', { detail: { id } })
    window.dispatchEvent(event)
  }

  return (
    <div className="toast-container">
      {activeToasts.map(toast => (
        <div key={toast.id} className={`toast toast-${toast.type}`}>
          <div className="toast-icon">{getIcon(toast.type)}</div>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            {toast.timestamp && (
              <span className="toast-timestamp">{formatTimestamp(toast.timestamp)}</span>
            )}
          </div>
          <div className="toast-actions">
            {toast.action && (
              <button
                onClick={() => {
                  toast.action?.onClick()
                  handleClose(toast.id)
                }}
                className={`toast-action-btn ${toast.action.variant === 'link' ? 'toast-action-link' : 'toast-action-primary'}`}
              >
                {toast.action.label}
              </button>
            )}
            <button
              onClick={() => handleClose(toast.id)}
              className="toast-close"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {toast.duration && toast.duration > 0 && (
            <div
              className="toast-progress"
              style={{ animationDuration: `${toast.duration}ms` }}
            />
          )}
        </div>
      ))}
    </div>
  )
}
